import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { updateFileField } from "../../../ReduxStore/UserSlice";
import { showToast } from "../Util/Util";

const FileUploadField = ({
  name,
  label,
  onFileSelect,
  error,
  helperText,
  required = false,
  value,
}) => {
  const fileInputRef = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(value);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isRemoteFile, setIsRemoteFile] = useState(false);
  const [remoteFileUrl, setRemoteFileUrl] = useState("");
  const [remoteFileName, setRemoteFileName] = useState("");
  const dispatch = useDispatch();
  
  useEffect(() => {
    if (value) {
      if (value.remote === true && value.url) {
        setIsRemoteFile(true);
        setRemoteFileUrl(value.url || value.documentPath);
        setRemoteFileName(value.filename || "document");
      } else if (value instanceof File) {
        setUploadedFile(value);
        setIsRemoteFile(false);
      } else if (value.base64) {
        setUploadedFile(value);
        setIsRemoteFile(false);
      }
    } else {
      setUploadedFile(null);
      setIsRemoteFile(false);
      setRemoteFileUrl("");
      setRemoteFileName("");
    }
  }, [value, name]);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const validateFile = (file) => {
    if (isRemoteFile && remoteFileUrl) {
      return true;
    }
    
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showToast("File size should not exceed 5MB", "error");
        return false;
      }

      const isImage = file.type.match("image.*");
      const isPdf = file.type === "application/pdf";
      const isExcel =
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        (file.name &&
          (file.name.toLowerCase().endsWith(".xlsx") ||
            file.name.toLowerCase().endsWith(".xls")));

      if (!isImage && !isPdf && !isExcel) {
        showToast("Please upload only JPEG, PNG, PDF, or Excel files", "error");
        return false;
      }
      
      return true;
    }
    
    return false;
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!validateFile(file)) {
        return;
      }

      setIsRemoteFile(false);
      setRemoteFileUrl("");
      setRemoteFileName("");
      
      setUploadedFile(file);
      
      if (onFileSelect) {
        const completelyNewFile = file;
        onFileSelect(completelyNewFile);
      }
    }
  };

  const handleRemoveFile = () => {
    setIsRemoteFile(false);
    setRemoteFileUrl("");
    setRemoteFileName("");
console.log("Removing file for field:", name);
    setUploadedFile(null);

    dispatch(
      updateFileField({
        field: name,
        value: null,
      })
    );

    if (onFileSelect) {
      onFileSelect(null);
    }
  };

  const handlePreviewDocument = () => {
    if (isRemoteFile && remoteFileUrl) {
      setPreviewUrl(remoteFileUrl);
      setPreviewOpen(true);
    } else if (uploadedFile) {
      const fileUrl = URL.createObjectURL(uploadedFile);
      setPreviewUrl(fileUrl);
      setPreviewOpen(true);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl && !isRemoteFile) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl("");
  };
  
  const hasFile = isRemoteFile || !!uploadedFile;
  const displayFileName = isRemoteFile ? remoteFileName : uploadedFile?.name;
  const isExcelDocument = isRemoteFile
    ? remoteFileName?.toLowerCase().endsWith(".xlsx") ||
      remoteFileName?.toLowerCase().endsWith(".xls")
    : uploadedFile &&
      (uploadedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        uploadedFile.type === "application/vnd.ms-excel" ||
        uploadedFile.name?.toLowerCase().endsWith(".xlsx") ||
        uploadedFile.name?.toLowerCase().endsWith(".xls"));

  return (
    <Box
      sx={{
        border: `1px solid ${error ? "#d32f2f" : "#E0E0E0"}`,
        borderRadius: 1,
        p: 2,
        mb: 2,
        backgroundColor: "white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="body1">
            {label}
            {required && <span style={{ color: "red" }}>*</span>}
          </Typography>
          {hasFile && (
            <Typography
              variant="caption"
              color="success.main"
              sx={{ display: "flex", alignItems: "center", mt: 0.5 }}
            >
              <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {isRemoteFile ? `${displayFileName} (from server)` : displayFileName}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {hasFile && (
            <>
              <IconButton
                size="small"
                onClick={handlePreviewDocument}
                color="primary"
                title="View Document"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleRemoveFile}
                title="Remove Document"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          )}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
          accept="image/*,application/pdf,.xls,.xlsx"
            style={{ display: "none" }}
          />
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={handleButtonClick}
            sx={{
              textTransform: "none",
              borderColor: error ? "#d32f2f" : undefined,
              color: error ? "#d32f2f" : undefined,
            }}
          >
            {hasFile ? "Replace" : "Upload"}
          </Button>
        </Box>
      </Box>
      {error && helperText && (
        <Typography color="error" variant="caption" sx={{ mt: 1 }}>
          {helperText}
        </Typography>
      )}

      {/* Document Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Document Preview
          <IconButton
            aria-label="close"
            onClick={handleClosePreview}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {previewUrl && (isRemoteFile || uploadedFile?.type?.includes("image")) && !isExcelDocument ? (
            <img
              src={previewUrl}
              alt="Document preview"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                display: "block",
                margin: "0 auto",
              }}
            />
          ) : previewUrl && uploadedFile?.type === "application/pdf" ? (
            <iframe
              src={previewUrl}
              title="PDF preview"
              width="100%"
              height="500px"
              style={{ border: "none" }}
            />
          ) : previewUrl && isExcelDocument ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ mb: 2 }}>
                Preview for Excel files isn&apos;t supported. You can download and
                open the file locally.
              </Typography>
              <Button
                variant="contained"
                component="a"
                href={previewUrl}
                download={displayFileName || "document.xlsx"}
              >
                Download File
              </Button>
            </Box>
          ) : (
            <Typography>Unable to preview this file type.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileUploadField;
